/**
 * PATH TO GO — distance-to-GO + per-pillar projection.
 *
 * Three signals, in increasing fidelity:
 *
 *   1. `distanceToGo(decisionScore)` — how far the site's decision score sits
 *      above the GO ceiling (decisionScore 2.0). Cheap scalar.
 *
 *   2. `perPillarMarginalDrag(composite)` — the LINEAR "what's holding it back"
 *      ranking: `(5 - score) × weight` per pillar. This is an ESTIMATE used
 *      only for RANKING which pillar to fix first; it is NOT the band outcome.
 *
 *   3. `projectPillarShift(...)` — the EXACT "fix this pillar → new band". It
 *      re-runs the REAL `computeSiteComposite` oracle and substitutes ONE
 *      pillar's benefit-aggregation contribution, then re-bands via the REAL
 *      `bandForBenefitComposite`. NOT a linear approximation.
 *
 * ── Why the exact projection is faithful (the adapter contract) ───────────
 * `computeSiteComposite` computes (Part C SP4 (S4) — HARD_MISSING aggregate):
 *     rawBenefit   = Σ(pillar.score × pillar.weight) / Σ(pillar.weight)
 *                    over the PRESENT (non-missing) PILLAR_IDS, using each
 *                    pillar's PRE-overwrite score. MISSING pillars are EXCLUDED
 *                    from both the sum and the weight denominator.
 *     composite    = clamp(rawBenefit − dataCompletenessPenalty, 0, 5)
 *     decisionScore= toDecisionScore(composite)
 *     band         = bandForBenefitComposite(composite, bandConfig)
 *
 * The ONLY pillar whose returned score differs from the value used in
 * `rawBenefit` is `evidenceQuality`: the oracle OVERWRITES its returned score
 * with `dcBenefit` (a function of the penalty) AFTER aggregation. In the
 * aggregation it contributes its pre-overwrite stub score of 0, and it is
 * always non-missing, so it is always included. So to reconstruct `rawBenefit`
 * EXACTLY from a returned `SiteComposite` we treat `evidenceQuality` as score 0
 * in the sum, SKIP missing pillars (mirroring HARD_MISSING), and use each other
 * present pillar's returned score. This reconstruction reproduces
 * `composite.composite` to floating-point exactness (validated in tests: no-op
 * round-trip diff 0, tolerance 1e-9).
 *
 * `projectPillarShift` therefore: reconstructs `rawBenefit`, substitutes the
 * target pillar's aggregation score with the hypothetical `newScore`, applies
 * the SAME `applyPenaltyToComposite` (which clamps identically), and re-bands
 * via the SAME `bandForBenefitComposite`. For a no-op substitution it returns
 * the oracle's own band/score — round-trip identity, by construction and by
 * test.
 *
 * Pure / deterministic. Imports + composes the portfolio engine; does NOT
 * re-implement scoring.
 */
import type { SiteComposite } from '../../types/portfolioComposite';
import { applyPenaltyToComposite } from '../portfolio/compositeScorer';
import {
  bandForBenefitComposite,
  toDecisionScore,
  type DecisionBand,
  type DecisionBandConfig,
} from '../portfolio/decisionBands';
import {
  PILLAR_IDS,
  isDecisionApplicablePillar,
  type PillarId,
} from '../portfolio/pillarRegistry';

/** The GO ceiling on the decision scale (GO band = decisionScore ∈ [1.0, 2.0]). */
export const GO_DECISION_CEILING = 2.0;

/**
 * Distance to the GO ceiling on the decision scale. 0 when the site is already
 * GO-or-better (decisionScore ≤ 2.0); otherwise how far the decision score sits
 * above the ceiling (positive = work remaining).
 */
export function distanceToGo(decisionScore: number): number {
  return Math.max(0, decisionScore - GO_DECISION_CEILING);
}

/** One pillar's linear marginal-drag estimate. */
export interface PillarMarginalDrag {
  pillarId: PillarId;
  /** Current benefit score (0–5) of the pillar. */
  score: number;
  /** Pillar weight in the composite. */
  weight: number;
  /** `(5 - score) × weight` — higher = drags the composite down more. ESTIMATE. */
  drag: number;
  /** True when the pillar carries no real data (worst-case imputed). */
  missing: boolean;
}

/**
 * Per-pillar LINEAR marginal drag, ranked worst-first.
 *
 * `drag = (5 − score) × weight` — the first-order contribution each pillar
 * makes to the gap between the site's benefit composite and a perfect 5.0. A
 * cheap, monotone "what's holding it back" signal for ORDERING which pillar to
 * fix next. It is explicitly an ESTIMATE for ranking — the band consequence of
 * actually fixing a pillar must be read from `projectPillarShift` (the exact
 * oracle re-run), never from this linear term.
 */
export function perPillarMarginalDrag(composite: SiteComposite): PillarMarginalDrag[] {
  const rows: PillarMarginalDrag[] = [];
  for (const pillarId of PILLAR_IDS) {
    const p = composite.pillars[pillarId];
    // Exclude both static advisory pillars and site-specific inactive pillars.
    // Ranking either as "holding the site back" would send the user to work
    // that cannot move the current decision.
    if (!isDecisionApplicablePillar(pillarId, p)) continue;
    if (!p) continue;
    const score = p.score;
    const weight = p.weight;
    rows.push({
      pillarId,
      score,
      weight,
      drag: (5 - score) * weight,
      missing: p.missing,
    });
  }
  // Rank worst (largest drag) first; tie-break by descending weight then id for
  // determinism.
  rows.sort((a, b) => {
    if (b.drag !== a.drag) return b.drag - a.drag;
    if (b.weight !== a.weight) return b.weight - a.weight;
    return a.pillarId.localeCompare(b.pillarId);
  });
  return rows;
}

/**
 * The aggregation score a pillar contributes to `rawBenefit` inside
 * `computeSiteComposite`. Identical to the returned `pillars[pid].score` for
 * every pillar EXCEPT `evidenceQuality`, whose returned score is overwritten
 * post-aggregation (it contributes 0 to the aggregate). Exposed so the
 * round-trip identity (`projectPillarShift` with the pillar's own aggregation
 * score === oracle band) is well-defined and testable.
 */
export function pillarAggregationScore(
  composite: SiteComposite,
  pillarId: PillarId,
): number {
  if (pillarId === 'evidenceQuality') return 0;
  return composite.pillars[pillarId]?.score ?? 0;
}

/**
 * Reconstruct the EXACT `rawBenefit` (pre-penalty weighted benefit composite)
 * the oracle computed, from a returned `SiteComposite`. Uses each pillar's
 * aggregation score (see `pillarAggregationScore`). Validated to reproduce
 * `composite.composite` after `applyPenaltyToComposite` to within 1e-9.
 */
function reconstructRawBenefit(composite: SiteComposite): number {
  let sum = 0;
  let w = 0;
  for (const pillarId of PILLAR_IDS) {
    const p = composite.pillars[pillarId];
    // Mirror `aggregateBenefitComposite`: static advisory and site-specific
    // inactive pillars are outside the decision denominator.
    if (!isDecisionApplicablePillar(pillarId, p)) continue;
    if (!p) continue;
    // Part C SP4 (S4) — mirror the HARD_MISSING aggregate: exclude missing
    // pillars from both the sum and the weight denominator.
    if (p.missing) continue;
    sum += pillarAggregationScore(composite, pillarId) * p.weight;
    w += p.weight;
  }
  return w > 0 ? sum / w : 0;
}

/** Result of an exact per-pillar projection. */
export interface PillarShiftProjection {
  pillarId: PillarId;
  /** The pillar's current aggregation score (pre-overwrite, 0–5). */
  fromScore: number;
  /** The hypothetical substituted score (0–5). */
  toScore: number;
  /** Benefit composite BEFORE the substitution (== oracle composite). */
  fromComposite: number;
  /** Benefit composite AFTER the substitution (penalty re-applied + clamped). */
  toComposite: number;
  /** Decision score after substitution (`toDecisionScore`). */
  decisionScore: number;
  /** Decision band after substitution (REAL `bandForBenefitComposite`). */
  band: DecisionBand;
  /** True when the substituted band differs from the oracle's current band. */
  bandChanged: boolean;
}

/**
 * EXACT projection: substitute ONE pillar's aggregation score with `newScore`
 * and return the resulting band/score, re-derived through the REAL portfolio
 * engine (`applyPenaltyToComposite` + `bandForBenefitComposite`), NOT a linear
 * approximation.
 *
 * @param composite  the oracle baseline — MUST be the output of the real
 *                   `computeSiteComposite` (the projection re-bands relative to
 *                   THIS composite's penalty + pillar weights).
 * @param pillarId   the pillar to substitute.
 * @param newScore   the hypothetical benefit score (0–5) for that pillar.
 * @param bandConfig optional decision-band config (defaults to DEFAULT_BANDS).
 *
 * Round-trip identity: `projectPillarShift(c, pid, pillarAggregationScore(c, pid))`
 * yields `toComposite === c.composite` and `band` == the band of `c.composite`
 * for a PRESENT (non-missing) pillar. For a MISSING pillar the substitution
 * models FIXING it (it becomes measured at `newScore`), so it ENTERS the
 * weighted aggregate — which is the whole point of the "fix this pillar → new
 * band" projection.
 */
export function projectPillarShift(
  composite: SiteComposite,
  pillarId: PillarId,
  newScore: number,
  bandConfig?: DecisionBandConfig | null,
): PillarShiftProjection {
  const fromScore = pillarAggregationScore(composite, pillarId);

  // Reconstruct the oracle's exact rawBenefit (Part C SP4 (S4) — HARD_MISSING:
  // missing pillars are excluded from the denominator), then swap the one
  // pillar's weighted contribution. The TARGET pillar is always included at
  // `newScore` (a fixed/hypothetical pillar is, by definition, now present);
  // every OTHER pillar is included only when non-missing — mirroring the oracle
  // aggregate. So Σweight grows by the target's weight only when the target was
  // previously missing (fixing it adds it to the basis); for a present target
  // Σweight is invariant and the round-trip identity holds.
  let sum = 0;
  let w = 0;
  for (const pid of PILLAR_IDS) {
    const p = composite.pillars[pid];
    // Non-decision pillars never enter the aggregate, even as the substitution
    // target. A hypothetical improvement therefore remains an honest no-op.
    if (!isDecisionApplicablePillar(pid, p)) continue;
    if (!p) continue;
    if (pid === pillarId) {
      sum += newScore * p.weight;
      w += p.weight;
      continue;
    }
    if (p.missing) continue;
    sum += pillarAggregationScore(composite, pid) * p.weight;
    w += p.weight;
  }
  const rawBenefit = w > 0 ? sum / w : 0;

  // Apply the SAME penalty + clamp the oracle applied, then re-band through the
  // SAME band function. This is the exactness guarantee.
  const toComposite = applyPenaltyToComposite(rawBenefit, composite.dataCompletenessPenalty);
  const band = bandForBenefitComposite(toComposite, bandConfig);
  const decisionScore = toDecisionScore(toComposite);

  // The oracle's current band (for change detection) read through the SAME
  // function so the comparison is internally consistent.
  const currentBand = bandForBenefitComposite(composite.composite, bandConfig);

  return {
    pillarId,
    fromScore,
    toScore: newScore,
    fromComposite: composite.composite,
    toComposite,
    decisionScore,
    band,
    bandChanged: band.id !== currentBand.id,
  };
}

/**
 * Reconstruct the rawBenefit publicly (for tests / diagnostics) so callers can
 * assert the no-op identity `applyPenaltyToComposite(reconstructRawBenefit(c),
 * c.dataCompletenessPenalty) === c.composite`.
 */
export function reconstructRawBenefitForTest(composite: SiteComposite): number {
  return reconstructRawBenefit(composite);
}
